/**
 * Compliance Leakage Card (MA-UI-107)
 * 
 * Shows dollar amount of ad spend tied to non-deployable leads.
 * Primary economic metric for compliance impact.
 */

import React from 'react';
import { DollarSign, TrendingDown, TrendingUp, Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

// =============================================================================
// COMPLIANCE LEAKAGE CARD
// =============================================================================

export function ComplianceLeakageCard({ data, isLoading }) {
  // Loading state
  if (isLoading) {
    return (
      <Card className="glass border-white/10 border-l-4 border-l-red-500/50">
        <CardContent className="p-5">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-slate-700 rounded w-1/2"></div>
            <div className="h-10 bg-slate-700 rounded w-2/3"></div>
            <div className="h-2 bg-slate-700 rounded w-full"></div>
            <div className="h-3 bg-slate-700 rounded w-3/4"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (!data) {
    return (
      <Card className="glass border-white/10 border-l-4 border-l-slate-500/50">
        <CardContent className="p-5">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-300">Compliance Leakage</span>
            </div>
            <p className="text-2xl font-bold text-slate-400">—</p>
            <p className="text-xs text-slate-500">
              Not enough lead and spend data yet to estimate leakage.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { amount, blocked_leads, estimated_cpl, delta_percent, breakdown } = data;
  
  // MA-AN-111: Handle insufficient data case (amount is null)
  if (amount === null || amount === undefined) {
    return (
      <Card className="glass border-white/10 border-l-4 border-l-slate-500/50" data-testid="compliance-leakage-card">
        <CardContent className="p-5">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-slate-500/10">
                <DollarSign className="h-4 w-4 text-slate-400" />
              </div>
              <span className="text-sm font-medium text-slate-300">Compliance Leakage</span>
            </div>
            <p className="text-2xl font-bold text-slate-400">—</p>
            <p className="text-xs text-slate-500">
              Insufficient compliance data to calculate leakage. Leads must have compliance eligibility fields set.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const isImproving = delta_percent !== null && delta_percent < 0;
  
  // Calculate breakdown percentages for mini-bar
  const total = (breakdown?.deployable || 0) + (breakdown?.quarantined || 0) + 
                (breakdown?.missing_trustedform || 0) + (breakdown?.other_blocked || 0);
  
  const deployablePercent = total > 0 ? (breakdown?.deployable / total) * 100 : 0;
  const quarantinedPercent = total > 0 ? (breakdown?.quarantined / total) * 100 : 0;
  const missingTfPercent = total > 0 ? (breakdown?.missing_trustedform / total) * 100 : 0;

  return (
    <Card 
      className="glass border-white/10 border-l-4 border-l-red-500/50"
      data-testid="compliance-leakage-card"
    >
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-red-500/10">
              <DollarSign className="h-4 w-4 text-red-400" />
            </div>
            <span className="text-sm font-medium text-slate-300">Compliance Leakage</span>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-3.5 w-3.5 text-slate-500" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p className="text-xs">
                  Compliance Leakage estimates how much marketing spend was consumed by leads 
                  that were not deployable due to compliance-related issues.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Primary Metric */}
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-3xl font-bold text-red-400">
            ${amount?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
          
          {/* Trend indicator */}
          {delta_percent !== null && delta_percent !== 0 && (
            <div className={`flex items-center gap-0.5 text-xs ${
              isImproving ? 'text-emerald-400' : 'text-red-400'
            }`}>
              {isImproving ? (
                <TrendingDown className="h-3 w-3" />
              ) : (
                <TrendingUp className="h-3 w-3" />
              )}
              <span>{Math.abs(delta_percent).toFixed(1)}%</span>
            </div>
          )}
        </div>

        {/* Helper text */}
        <p className="text-xs text-slate-400 mb-4">
          Estimated ad spend tied to leads your team could not legally or operationally use.
        </p>

        {/* Secondary stats */}
        <div className="flex items-center gap-4 text-xs text-slate-400 mb-3">
          <div>
            <span className="text-slate-500">Blocked Leads:</span>{' '}
            <span className="text-slate-300 font-medium">{blocked_leads?.toLocaleString()}</span>
          </div>
          <div>
            <span className="text-slate-500">Est. CPL:</span>{' '}
            <span className="text-slate-300 font-medium">${estimated_cpl?.toFixed(0)}</span>
          </div>
        </div>

        {/* Mini breakdown bar */}
        <div className="space-y-1.5">
          <div className="h-2 rounded-full bg-slate-700/50 overflow-hidden flex">
            <div 
              className="bg-emerald-500/80 transition-all" 
              style={{ width: `${deployablePercent}%` }}
            />
            <div 
              className="bg-amber-500/80 transition-all" 
              style={{ width: `${quarantinedPercent}%` }}
            />
            <div 
              className="bg-red-500/80 transition-all" 
              style={{ width: `${missingTfPercent}%` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-slate-500">
            <span>Deployable</span>
            <span>Quarantined</span>
            <span>Missing TF</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default ComplianceLeakageCard;
